import {Database} from '@tnc/shared';
import clsx from 'clsx';
import {useState} from 'react';
import {Link} from 'react-router';

import {usdFormatter} from '@/lib/helpers';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {useExchangeOrder} from '../providers/exchangeOrder';

type BuyOrderCardProps = {
  onOpenProductSelection: (marketId: number, brandId: number) => void;
  onOpenMarketSearch: () => void;
  onOpenBrandSearch: () => void;
  selectedMarket: {market_id: number; market_name: string} | null;
  selectedBrand: Database['public']['Functions']['get_brands_positions_aggregates']['Returns'][0] | null;
  onPositionFilled?: () => void;
};

function BuyOrderCard({
  onOpenProductSelection,
  onOpenMarketSearch,
  onOpenBrandSearch,
  selectedMarket,
  selectedBrand,
  onPositionFilled,
}: BuyOrderCardProps) {
  // Hooks
  const {accountBalance, getUserAccountBalance} = useAuth();
  const {buyOrderProduct, submitBuyOrder, resetForms} = useExchangeOrder();
  const {toast} = useNotifications();

  // State
  const [limitPrice, setLimitPrice] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [orderType, setOrderType] = useState<'limit' | 'market'>('limit');
  const [isLoading, setIsLoading] = useState(false);

  // ************************************************** */
  // ******************* QUERYS ******************* */
  // ************************************************** */

  /************************************************** */
  /****************** FUNCTIONALITY ******************* */
  /************************************************** */
  const resetForm = () => {
    setLimitPrice(0);
    setQuantity(0);
    setOrderType('limit');
  };

  const handleSubmitBuyOrder = async () => {
    setIsLoading(true);
    try {
      const result = await submitBuyOrder({
        quantity,
        limitPrice,
        orderType,
        marketId: selectedMarket?.market_id || 0,
      });

      if (result.success) {
        onPositionFilled?.();
        toast.success('Buy order submitted successfully');
        await getUserAccountBalance();
        resetForm();
        resetForms();
      } else {
        toast.error(result.error || 'Failed to submit buy order');
      }
    } catch (error) {
      console.error('Error submitting buy order', error);
      toast.error('Failed to submit buy order');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="box custom-box z-40">
        <div className="box-header">
          <div className="box-title">Buy Order</div>
        </div>
        <div className="box-body">
          <div>
            <div className="flex flex-col gap-2">
              <div className="flex w-full gap-2">
                {/* Market Selection */}
                <div
                  onClick={onOpenMarketSearch}
                  data-hs-overlay="#hs-market-modal"
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-2 rounded-sm border p-2',
                    'hover:bg-gray-50 dark:hover:bg-gray-800'
                  )}
                >
                  {selectedMarket ? (
                    <div className="flex items-center gap-2">
                      <span>{selectedMarket.market_name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500">
                      <i className="ri-search-line"></i>
                      <span>Search Markets...</span>
                    </div>
                  )}
                </div>

                {/* Brand Selection */}
                <div
                  onClick={selectedMarket ? onOpenBrandSearch : undefined}
                  data-hs-overlay="#hs-brand-modal"
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-2 rounded-sm border p-2',
                    !selectedMarket && 'cursor-not-allowed opacity-50',
                    'hover:bg-gray-50 dark:hover:bg-gray-800'
                  )}
                >
                  {selectedBrand ? (
                    <div className="flex items-center gap-2">
                      <span>{selectedBrand.brand_name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500">
                      <i className="ri-search-line"></i>
                      <span>Search Brands...</span>
                    </div>
                  )}
                </div>
              </div>

              {!buyOrderProduct ? (
                <button
                  type="button"
                  className="ti-btn ti-btn-disabled-full btn-wave ti-btn-md mt-2 dark:border-defaultborder/10"
                  disabled={!selectedBrand || !selectedMarket}
                  data-hs-overlay="#hs-extralarge-modal"
                  onClick={() => onOpenProductSelection(selectedMarket?.market_id || 0, selectedBrand?.brand_id || 0)}
                >
                  <i className="ri-shopping-cart-line me-2"></i>
                  Select Product
                </button>
              ) : (
                <div className="my-2 flex items-center justify-between">
                  <div className="flex w-40 items-center">
                    <img
                      src={buyOrderProduct.product_logo || buyOrderProduct.brand_logo || '/images/logo/logo.png'}
                      alt=""
                      className="avatar avatar-md !mb-0 me-2 rounded-md leading-none"
                    />
                    <div>
                      <p className="mb-0 font-semibold">
                        <Link to="#" title="nft_name">
                          {buyOrderProduct.product_name}
                        </Link>
                      </p>
                      <Link
                        to="#"
                        className="text-[0.75rem] font-normal text-[#8c9097] dark:text-white/50"
                        title="creator_name"
                      >
                        {buyOrderProduct.brand_name}
                      </Link>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ti-btn ti-btn-outline-primary btn-wave ti-btn-sm"
                    data-hs-overlay="#hs-extralarge-modal"
                    onClick={() => onOpenProductSelection(selectedMarket?.market_id || 0, selectedBrand?.brand_id || 0)}
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                </div>
              )}
            </div>
            {buyOrderProduct && (
              <div>
                <div className="flex justify-between py-2 text-[.875rem]">
                  <div>
                    <span className="font-semibold text-dark">Price Low:</span>
                    <span
                      onClick={() => setLimitPrice(buyOrderProduct?.min_price || 0)}
                      className="ms-2 inline-block text-[.875rem] text-success"
                    >
                      {buyOrderProduct?.min_price ? `${usdFormatter.format(buyOrderProduct.min_price)}` : 'N/A'}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                  <div>
                    <span className="font-semibold text-dark">Price High:</span>
                    <span
                      onClick={() => setLimitPrice(buyOrderProduct?.max_price || 0)}
                      className="ms-2 inline-block text-[#8c9097] text-danger"
                    >
                      {buyOrderProduct?.max_price ? `${usdFormatter.format(buyOrderProduct.max_price)}` : 'N/A'}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                </div>
                <div className="flex justify-between py-2 text-[.875rem]">
                  <div>
                    <span className="font-semibold text-dark">Factored Price:</span>
                    <span className="ms-2 inline-block text-[.875rem] text-primary">
                      {buyOrderProduct.min_factored_price
                        ? `${usdFormatter.format(buyOrderProduct.min_factored_price)}`
                        : 'N/A'}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                </div>
                <div className="py-2 text-[.875rem] font-semibold">
                  Market Volume:{' '}
                  <span
                    className="inline-block text-[.875rem]"
                    onClick={() => setQuantity(buyOrderProduct?.item_count || 0)}
                  >
                    {buyOrderProduct?.item_count ? buyOrderProduct.item_count : 'N/A'} x{' '}
                    <span className="inline-block text-[.875rem] font-normal text-[#8c9097] dark:text-white/50">
                      {buyOrderProduct?.product_name}
                    </span>
                  </span>
                </div>
              </div>
            )}
            {/*  LIMIT AND QUANTITY INPUTS */}
            {buyOrderProduct && (
              <div className="flex flex-col gap-4">
                {/* ORDER TYPE */}
                <div className="input-group crypto-buy-sell mt-3 flex-1 flex-col">
                  <label className="mb-2 text-[0.75rem] font-semibold">Order Type</label>
                  <div className="flex gap-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="limit"
                        name="orderType"
                        value="limit"
                        checked={orderType === 'limit'}
                        onChange={e => setOrderType(e.target.value as 'limit' | 'market')}
                        className="h-4 w-4 border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary"
                      />
                      <label htmlFor="limit" className="ml-2 text-sm font-medium">
                        Limit Order
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="market"
                        name="orderType"
                        value="market"
                        disabled
                        checked={orderType === 'market'}
                        onChange={e => setOrderType(e.target.value as 'limit' | 'market')}
                        className="h-4 w-4 border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary"
                      />
                      <label htmlFor="market" className="ml-2 text-sm font-medium text-gray-500">
                        Market Order
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  {/* PRICE LIMIT */}
                  <div className="crypto-buy-sell flex-1">
                    <label className="mb-2 flex items-stretch gap-1 text-[0.75rem] font-semibold">
                      Limit Price (USD)
                      <i
                        className="ri-information-line cursor-help"
                        title="Set the maximum price you want to buy items for. Market orders will buy at the current best available price."
                      ></i>
                    </label>
                    {orderType === 'limit' ? (
                      <div className="relative flex w-full items-stretch justify-center gap-1 !rounded-md border border-defaultborder/10 p-1">
                        <button
                          type="button"
                          className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                          onClick={() => setLimitPrice(limitPrice > 0 ? limitPrice - 1 : limitPrice)}
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <input
                          type="number"
                          className="form-control border-none px-1 py-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          placeholder="Limit Price (USD)"
                          value={limitPrice || ''}
                          min={0}
                          max={buyOrderProduct?.max_factored_price}
                          onChange={e => {
                            const newValue = Number(e.target.value);
                            setLimitPrice(Math.min(newValue, buyOrderProduct?.max_factored_price || 0));
                          }}
                        />
                        <button
                          type="button"
                          className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                          onClick={() =>
                            setLimitPrice(
                              limitPrice < (buyOrderProduct?.max_factored_price || 0) ? limitPrice + 1 : limitPrice
                            )
                          }
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="pt-1 text-center text-lg font-semibold text-primary">
                        {usdFormatter.format(buyOrderProduct?.max_factored_price)}
                      </div>
                    )}
                  </div>

                  {/* QUANTITY */}
                  <div className="crypto-buy-sell flex-1">
                    <label className="mb-2 flex items-center gap-1 text-[0.75rem] font-semibold">
                      Quantity
                      <i className="ri-information-line cursor-help" title="The number of items you want to buy"></i>
                    </label>

                    <div className="relative flex w-full items-stretch justify-center gap-1 !rounded-md border border-defaultborder/10 p-1">
                      <button
                        type="button"
                        className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                        onClick={() => setQuantity(quantity > 0 ? quantity - 1 : quantity)}
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        type="number"
                        className="form-control border-none px-1 py-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="Quantity"
                        value={quantity}
                        min={0}
                        onChange={e => setQuantity(Number(e.target.value))}
                      />
                      <button
                        type="button"
                        className="hover:bg-red-600 aspect-square flex-none rounded-sm bg-primary p-1 px-1.5 text-white transition-colors"
                        onClick={() => setQuantity(quantity + 1)}
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* POTENTIAL PROFIT */}
                <div className="flex justify-between py-2 text-[.875rem]">
                  <div>
                    <span className="font-semibold text-dark">Potential Profit:</span>
                    <span className="ms-2 inline-block text-[.875rem] text-primary">
                      {usdFormatter.format((buyOrderProduct.max_factored_price - limitPrice) * quantity)}
                    </span>
                    <span className="ms-2 font-semibold text-dark">USD</span>
                  </div>
                </div>
              </div>
            )}

            {/* Total Cost Breakdown */}
            <div className="mt-4 space-y-2 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
              <div className="flex justify-between">
                <span className="text-[0.875rem] text-gray-600 dark:text-gray-300">Subtotal:</span>
                {orderType === 'limit' && (
                  <span className="text-[0.875rem] font-medium">{usdFormatter.format(limitPrice * quantity)}</span>
                )}
                {orderType === 'market' && buyOrderProduct?.max_factored_price && (
                  <span className="text-[0.875rem] font-medium">
                    {usdFormatter.format(buyOrderProduct.max_factored_price * quantity)}
                  </span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-[0.875rem] text-gray-600 dark:text-gray-300">TNC Fee (2%):</span>
                {orderType === 'limit' && (
                  <span className="text-[0.875rem] font-medium">
                    {usdFormatter.format(limitPrice * quantity * 0.02)}
                  </span>
                )}
                {orderType === 'market' && buyOrderProduct?.max_factored_price && (
                  <span className="text-[0.875rem] font-medium">
                    {usdFormatter.format(buyOrderProduct.max_factored_price * quantity * 0.02)}
                  </span>
                )}
              </div>
              <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                <div className="flex justify-between">
                  <span className="text-[0.875rem] font-medium">Total:</span>
                  {orderType === 'limit' && (
                    <span className="text-[0.875rem] font-bold text-success">
                      {usdFormatter.format(limitPrice * quantity * 1.02)}
                    </span>
                  )}
                  {orderType === 'market' && buyOrderProduct?.max_factored_price && (
                    <span className="text-[0.875rem] font-bold text-success">
                      {usdFormatter.format(buyOrderProduct.max_factored_price * quantity * 1.02)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="mb-2 mt-4 text-[0.75rem] font-semibold">SELECT PAYMENT METHOD :</label>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 xl:col-span-12">
                  <div className="rounded-md border !p-2 dark:border-defaultborder/10">
                    <div className="form-check !mb-0 !min-h-[auto] !ps-0">
                      <input
                        className="form-check-input !align-middle"
                        type="radio"
                        name="flexRadioDefault"
                        id="flexRadioDefault3"
                        defaultChecked
                      />
                      <label className="form-check-label text-[0.75rem]" htmlFor="flexRadioDefault3">
                        Account Balance: {usdFormatter.format(accountBalance)}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid">
              <button
                type="button"
                className="ti-btn ti-btn-success-full btn-wave ti-btn-lg dark:border-defaultborder/10"
                disabled={
                  !selectedBrand || !selectedMarket || !quantity || (orderType === 'limit' && !limitPrice) || isLoading
                }
                onClick={handleSubmitBuyOrder}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </span>
                ) : (
                  'BUY'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BuyOrderCard;
